import { useEffect, useState } from 'react';
import { Badge, Card, Cell, Image, Spacer, Text } from '@vinted/web-ui';
import classNames from 'classnames';
import useTranslate from 'hooks/useTranslate';
import useTracking from 'hooks/useTracking';
import { STATIC_ASSETS_URL } from 'constants/index';
import { EDIT_FEATURED_COLLECTION_URL, MEMBER_PROFILE_URL } from 'constants/routes';
import { clickListItemEvent } from 'libs/common/event-tracker/events';
import { ContentSource } from 'constants/tracking/content-sources';
import { ListItemContentType } from 'constants/tracking/content-types';
import { navigateToPage } from 'libs/utils/window';
import CollectionViewModal from '../CollectionViewModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EMPTY_COLLECTION_IMAGE_PATH = "".concat(STATIC_ASSETS_URL, "/assets/no-photo/hanger-empty-state-96.svg");

const CollectionBox = _ref => {
  let {
    collection,
    viewingSelf,
    index,
    userId,
    viewedCollectionId,
    nextPush
  } = _ref;
  const translate = useTranslate('featured_collection.multiple.collections_block');
  const {
    track
  } = useTracking();
  const {
    0: isCollectionViewModalOpen,
    1: setIsCollectionViewModalOpen
  } = useState(false);
  const {
    id,
    title,
    itemsCount,
    itemsPhotos,
    isActive,
    humanizedDiscountAmount
  } = collection;
  const openModalOnLoad = viewedCollectionId === id;

  const handleModalClose = () => {
    setIsCollectionViewModalOpen(false);
  };

  const handleModalCloseOnButtonClick = () => {
    if (nextPush) {
      nextPush(MEMBER_PROFILE_URL(userId));
    }

    handleModalClose();
  };

  const handleModalOpen = () => {
    setIsCollectionViewModalOpen(true);
  };

  useEffect(() => {
    if (!openModalOnLoad) handleModalClose();
    if (openModalOnLoad) handleModalOpen();
  }, [openModalOnLoad]);

  const renderImage = imgNumber => {
    return /*#__PURE__*/_jsx("div", {
      className: "multiple-collections-block__collage-image collage--image-".concat(imgNumber),
      children: /*#__PURE__*/_jsx(Image, {
        src: itemsPhotos[imgNumber - 1],
        ratio: Image.Ratio.Square,
        scaling: Image.Scaling.Cover,
        testId: "collections-box-collage-image-".concat(imgNumber)
      })
    });
  };

  const renderImages = () => {
    if (itemsPhotos.length === 0) {
      return /*#__PURE__*/_jsx(Card, {
        children: /*#__PURE__*/_jsx(Image, {
          src: EMPTY_COLLECTION_IMAGE_PATH,
          testId: "collections-box-empty-state",
          alt: "",
          scaling: Image.Scaling.ScaleDown
        })
      });
    }

    if (itemsPhotos.length < 3) {
      return renderImage(1);
    }

    return /*#__PURE__*/_jsxs("div", {
      className: "multiple-collections-block__collage",
      children: [renderImage(1), renderImage(2), renderImage(3)]
    });
  };

  const renderBadge = () => {
    if (!humanizedDiscountAmount) return null;
    return /*#__PURE__*/_jsx("div", {
      className: "multiple-collections-block__box-content",
      children: /*#__PURE__*/_jsx(Cell, {
        styling: Cell.Styling.Narrow,
        theme: "transparent",
        children: /*#__PURE__*/_jsx("div", {
          className: "u-flexbox u-flex-direction-column u-align-items-flex-start u-gap-small",
          children: /*#__PURE__*/_jsx(Badge, {
            theme: "warning",
            content: humanizedDiscountAmount,
            testId: "collection-box-discount-badge-".concat(id)
          })
        })
      })
    });
  };

  const handleNavigation = () => {
    navigateToPage(EDIT_FEATURED_COLLECTION_URL(id));
  };

  const handleCollectionClick = () => {
    const shouldRedirectToEdit = itemsCount === 0;
    track(clickListItemEvent({
      id,
      position: index + 1,
      contentType: ListItemContentType.Item,
      contentSource: ContentSource.FeaturedCollection
    }));

    if (!shouldRedirectToEdit) {
      if (nextPush) {
        nextPush(MEMBER_PROFILE_URL(userId, id));
      }

      return handleModalOpen();
    }

    return handleNavigation();
  };

  const renderNavigationOverlay = () => {
    return /*#__PURE__*/_jsx("div", {
      className: "multiple-collections-block__box-content u-flexbox u-fill-height u-fill-width",
      children: /*#__PURE__*/_jsx(Cell, {
        type: Cell.Type.Navigating,
        theme: "transparent",
        onClick: handleCollectionClick,
        testId: "collection-box-overlay"
      })
    });
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: classNames('u-flexbox u-flex-direction-column', {
        'multiple-collections-block__box--inactive': !isActive
      }),
      "data-testid": "collection-".concat(id),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "multiple-collections-block__square-box-container u-position-relative",
        children: [renderImages(), renderBadge(), renderNavigationOverlay()]
      }), /*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsxs("div", {
        className: "u-flexbox u-flex-direction-column",
        children: [/*#__PURE__*/_jsx("div", {
          className: "u-text-wrap multiple-collections-block__box-title",
          children: /*#__PURE__*/_jsx(Text, {
            text: title,
            type: Text.Type.Body,
            theme: "amplified",
            width: Text.Width.Parent
          })
        }), /*#__PURE__*/_jsx(Text, {
          text: translate('collection_item_count', {
            count: itemsCount
          }, {
            count: itemsCount
          }),
          type: Text.Type.Caption,
          theme: "muted"
        })]
      })]
    }), isCollectionViewModalOpen && /*#__PURE__*/_jsx(CollectionViewModal, {
      isShown: isCollectionViewModalOpen,
      onClose: handleModalCloseOnButtonClick,
      viewingSelf: viewingSelf,
      collectionId: id
    })]
  });
};

export default CollectionBox;